import { from, Observable } from "rxjs";
import { timeout, take } from "rxjs/operators";
import { stripData } from "./dataTableBackendHelper";
import {
  AppConfigService,
  ApiPathService,
  DataFilterService,
  QueuedHttpService,
  RecordsetAdapterService,
  TableAdapterService,
  HttpService,
  TableModel,
  FilterConfig,
  ApiResponse,
  TableDataResponse,
  CommentCountsResponse,
  FieldValuesRequestParams,
  DeleteFieldResponse,
} from "./types";

export class DataTableBackendService {
  constructor(
    private appConfig: AppConfigService,
    private apiPath: ApiPathService,
    private dataFilter: DataFilterService,
    private queuedHttp: QueuedHttpService,
    private recordsetAdapter: RecordsetAdapterService,
    private tableAdapter: TableAdapterService,
    private backendApi: HttpService
  ) {}

  /**
   * Gets table metadata for a given table identifier
   * @param tableIdentifier - The table identifier
   * @returns Promise resolving to table metadata
   */
  getTableMetadata(tableIdentifier: string | number): Promise<any> {
    const url = this.apiPath.table.getMetaDataUrl(tableIdentifier);
    return this.queuedHttp.get(url).then(response => response.data);
  }

  /**
   * Gets table data with optional metadata inclusion
   * @param tableIdentifier - The table identifier
   * @param filterConfig - Filter configuration object
   * @param includeMetadata - Whether to include metadata in the response
   * @returns Promise resolving to table data
   */
  getTableData(tableIdentifier: string | number, filterConfig: any, includeMetadata?: boolean): Promise<any> {
    const url = this.apiPath.table.getDataUrl(tableIdentifier);
    const requestBody = this.dataFilter.getFilterRequestBody(filterConfig);

    if (includeMetadata) {
      return Promise.all([
        this.getTableMetadata(tableIdentifier),
        this.queuedHttp.post(url, requestBody),
      ]).then(([metadata, tableData]) => ({ ...tableData.data, metaData: metadata }));
    }

    return this.queuedHttp.post(url, requestBody).then(response => response.data);
  }

  /**
   * Gets filtered record count for a table
   * @param tableIdentifier - The table identifier
   * @param filterConfig - Filter configuration object
   * @returns Promise resolving to record count data
   */
  getFilteredRecordCount(tableIdentifier: string | number, filterConfig: any): Promise<any> {
    const url = this.apiPath.table.getRecordCountUrl(tableIdentifier);
    const requestBody = this.dataFilter.getFilterRequestBody(filterConfig);
    return this.queuedHttp.post(url, requestBody).then(response => response.data);
  }

  /**
   * Gets comment counts for the current project/control/test
   * @returns Promise resolving to comment counts data
   */
  getCommentCounts(): Promise<CommentCountsResponse> {
    const url = this.appConfig.coneService.commentCountProjectControlControlTestInterpretationsPath({
      project_id: this.appConfig.project_id,
      control_id: this.appConfig.control_id,
      control_test_id: this.appConfig.control_test_id,
    });

    return this.backendApi.post(url).then(response => response.data);
  }

  /**
   * Requests field values for a specific column
   * @param tableIdentifier - The table identifier
   * @param columnName - Name of the column
   * @param fieldType - Type of the field
   * @param search - Optional search text
   * @param timezoneOffset - Optional timezone offset for datetime fields
   * @param position - Position for pagination
   * @returns Promise resolving to field values
   */
  fieldValuesRequested(
    tableIdentifier: string | number,
    columnName: string,
    fieldType: string,
    search?: string,
    timezoneOffset?: number,
    position?: number
  ): Promise<any> {
    let url: string;
    const params: FieldValuesRequestParams = {
      column_name: columnName,
      position: position || 0,
      limit: 200,
    };

    if (search) {
      url = this.apiPath.table.getColumnSearchUrl(tableIdentifier);
      params.search_text = search;
      if (fieldType === "datetime" && timezoneOffset !== undefined) {
        params.timezone_offset = timezoneOffset;
      }
    } else {
      url = this.apiPath.table.getColumnValuesUrl(tableIdentifier);
    }

    return this.queuedHttp.get(url, { params }).then(response => response.data);
  }

  /**
   * Requests table data for a control test
   * @param tableId - The table/control test ID
   * @returns Promise resolving to deserialized table model
   */
  tableRequested(tableId: string | number): Promise<TableModel> {
    const controlTestId = tableId;
    const controlId = this.appConfig.control_id;
    const projectId = this.appConfig.project_id;
    const url = this.apiPath.metric.getTableUrl(projectId, controlId, controlTestId);

    return this.queuedHttp.get(url).then(response => {
      const actualControlTestId = response.data.id;
      stripData(response.data);
      return this.tableAdapter.deserializeTable(actualControlTestId, response.data);
    });
  }

  /**
   * Requests table data for a metric
   * @param tableId - The table ID
   * @returns Promise resolving to deserialized table model
   */
  tableRequestedForMetric(tableId: string | number): Promise<TableModel> {
    const url = this.apiPath.metric.getTableForMetricUrl(tableId);
    return this.queuedHttp.get(url).then(response => {
      const controlTestId = response.data.id;
      return this.tableAdapter.deserializeTable(controlTestId, response.data);
    });
  }

  /**
   * Requests recordset data for a table with filters
   * @param table - The table model
   * @param filterConfig - Filter configuration
   * @returns Promise resolving to deserialized recordset
   */
  recordsetRequested(table: TableModel, filterConfig: FilterConfig): Promise<any> {
    return this.getTableData(table.id(), filterConfig.toJson()).then(data => {
      return this.recordsetAdapter.deserializeRecordset(table, filterConfig, data.result);
    });
  }

  /**
   * Deletes a field/attribute
   * @param fieldName - Name of the field to delete
   * @returns Promise resolving when deletion is complete
   */
  deleteField(fieldName: string): Promise<DeleteFieldResponse> {
    const attributeBaseUrl = ({
      projectId,
      controlId,
      controlTestId,
    }: {
      projectId: string | number;
      controlId: string | number;
      controlTestId: string | number;
    }) => `/projects/${projectId}/controls/${controlId}/control_tests/${controlTestId}/exception_attributes`;

    const ids = {
      projectId: this.appConfig.project_id,
      controlId: this.appConfig.control_id,
      controlTestId: this.appConfig.control_test_id,
    };

    const url = `${attributeBaseUrl(ids)}?field_name=${encodeURIComponent(fieldName)}`;

    return from(this.queuedHttp.delete(url))
      .pipe(timeout(60000), take(1))
      .toPromise() as Promise<DeleteFieldResponse>;
  }
}
